import { FC, useEffect, useRef, useState } from "react";
import { TableCell, TableRow, TableV2 } from "components";
import { useApi } from "hooks/useApi/useApiHooks";
import { IAcademicYear } from "../academic-years/types";
import { get } from "lodash";
import { AxiosResponse } from "axios";
import { MonthlyPaymentTableHead } from "./components/MonthlyPaymentTableHead";
import useQueryHook from "hooks/useQueryHook";
import TablePagination from "components/elements/table/components/tablePagination";
import { TablePaginationWrapper } from "components/elements/table/components/tablePagination.style";
import { TableNavbar } from "./components/TableNavbar";
import { IStudent } from "pages/school/archiveStudents/types";
import { Payments } from "./components/Payments";
import jsPDF from "jspdf"
import domtoimage from 'dom-to-image';
import * as XLSX from 'xlsx';
import { Tooltip } from "antd";


export interface IMonthlyPaymentProps { }

export const MonthlyPayment: FC<IMonthlyPaymentProps> = ({ }) => {
  const { data } = useApi<AxiosResponse<IAcademicYear[]>>(
    "academic-year",
    undefined
  );
  const [total, setTotal] = useState<number | undefined>();
  const { AddQueryParams, QueryParams } = useQueryHook();
  const { data: students } = useApi<IStudent>("/students/pagin", {
    page: get(QueryParams, "page", 1),
    limit: get(QueryParams, "limit", 50),
    state: "active",
    studentId: QueryParams.studentId,
  });

  useEffect(() => {
    setTotal(get(students, "data.total"));
  }, [students]);

  useEffect(() => {
    if (!QueryParams?.selectedYear) {
      const academicYears: IAcademicYear[] = get(data, "data.data", []);
      if (academicYears.length) {
        AddQueryParams({
          selectedYear: academicYears.find(
            (item: IAcademicYear) =>
              new Date(item.startsAt) <= new Date() &&
              new Date(item.endsAt) >= new Date()
          )?._id || academicYears[0]._id
        });
      }
    }
  }, [data]);

  const componentRef = useRef<any>(null);

  const handlePrint = async () => {
    const element = componentRef.current;
    if (!element) return;

    try {
      const dataUrl = await domtoimage.toPng(element, {
        bgcolor: '#ffffff',
        filter: (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            return element.id !== 'ignore';
          }
          return true;
        },
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });

      const imgProperties = pdf.getImageProperties(dataUrl);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;

      pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);

      pdf.save(`monthly-payment.pdf`);
    } catch (error) {
      console.error("Xatolik yuz berdi:", error);
    }
  };


  /// Excel

  const exportToExcel = () => {
    const table = componentRef.current;
    if (!table) {
      return;
    }

    const rows = table.querySelectorAll("tr");
    const data: (string | number)[][] = [];
    const mergedCells: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];

    let rowIndex = 0;
    rows.forEach((row: any) => {
      const rowData: (string | number)[] = [];
      const cells = row.querySelectorAll("td, th");
      let colIndex = 0;

      cells.forEach((cell: any) => {
        while (rowData[colIndex] !== undefined) {
          colIndex++; // Bo'sh bo'lmagan joyni o'tkazib yuboramiz
        }

        const input = cell.querySelector("input");
        const antdSelect = cell.querySelector(".ant-select-selector"); // AntD Select klassi
        let cellValue = cell.textContent?.trim() || "";

        if (input) {
          cellValue = input.value;
        } else if (antdSelect) {
          const selectedSpan = antdSelect.querySelector(".ant-select-selection-item");
          cellValue = selectedSpan ? selectedSpan.textContent?.trim() || "" : "";
        }

        rowData[colIndex] = cellValue; // Ma'lumot qo'shamiz

        const colspan = cell.getAttribute("colspan") ? parseInt(cell.getAttribute("colspan")) : 1;
        const rowspan = cell.getAttribute("rowspan") ? parseInt(cell.getAttribute("rowspan")) : 1;

        if (colspan > 1 || rowspan > 1) {
          mergedCells.push({
            s: { r: rowIndex, c: colIndex }, // Boshlanish joyi
            e: { r: rowIndex + rowspan - 1, c: colIndex + colspan - 1 }, // Tugash joyi
          });
        }

        // Colspan bo'lsa, keyingi ustunlarni ham band qilamiz
        for (let i = 1; i < colspan; i++) {
          rowData[colIndex + i] = "";
        }

        colIndex += colspan; // Keyingi ustunga o'tamiz
      });

      data.push(rowData);
      rowIndex++;
    });

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    worksheet["!merges"] = mergedCells; // Merged cell qo'shamiz

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet 1");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAsExcelFile(excelBuffer, "table_export.xlsx");
  };

  const saveAsExcelFile = (buffer: ArrayBuffer, fileName: string) => {
    const data = new Blob([buffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateIndex = (index: number) => {
    const page = get(QueryParams, "page", 1);
    const limit = get(QueryParams, "limit", 50);
    return (+page - 1) * +limit + index + 1;
  };

  const theme = localStorage.getItem('theme')

  return (
    <>
      <TableNavbar handlePrint={handlePrint} exportToExcel={exportToExcel} data={get(data, "data.data") as IAcademicYear[]} />
      <div className="h-[calc(100%-164px)] bg-bgMain overflow-auto">
        <TableV2 ref={componentRef}>
          {get(students, "data.data", []).length && <MonthlyPaymentTableHead />}
          {get(students, "data.data", [])
            .filter((student: IStudent) => {
              const isStudentMatch = QueryParams.studentId
                ? student._id === QueryParams.studentId
                : true;

              const isClassMatch = QueryParams.classId
                ? student.class?._id === QueryParams.classId
                : true;

              return isStudentMatch && isClassMatch;
            })
            .map((student: IStudent, index: number) => (
              <TableRow key={student._id}>
                <TableCell
                  className={`sticky left-0 z-10 w-[50px] min-w-[50px] max-w-[50px] ${theme === "light" ? "bg-[#F5F5F5]" : "bg-[#102231]"
                    } whitespace-nowrap overflow-hidden text-ellipsis`}
                >
                  {calculateIndex(index)}
                </TableCell>

                <TableCell
                  className={`sticky left-[49px] z-10 w-[200px] min-w-[200px] max-w-[200px] ${theme === "light" ? "bg-[#F5F5F5]" : "bg-[#102231]"
                    } whitespace-nowrap overflow-hidden text-ellipsis`}
                >
                  {`${student.firstName} ${student.lastName}`}
                </TableCell>

                <Tooltip title={`${student.class?.grade}-${student.class?.letter} ${student.class?.name}`}>
                  <TableCell
                    className={`sticky left-[248px] z-10 w-[100px] min-w-[100px] max-w-[100px] ${theme === "light" ? "bg-[#F5F5F5]" : "bg-[#102231]"
                      } whitespace-nowrap overflow-hidden text-ellipsis`}
                  >
                    {`${student.class?.grade}-${student.class?.letter} ${student.class?.name}`}
                  </TableCell>
                </Tooltip>

                <TableCell
                  className={`sticky left-[347px] z-10 w-[150px] min-w-[150px] max-w-[150px] ${theme === "light" ? "bg-[#F5F5F5]" : "bg-[#102231]"
                    } whitespace-nowrap overflow-hidden text-ellipsis`}
                >
                  {student?.parents?.[0]?.phoneNumber || student?.parents?.[1]?.phoneNumber}
                </TableCell>

                <Payments student={student} />
              </TableRow>
            ))}


        </TableV2>
      </div>
      <TablePaginationWrapper>
        <TablePagination
          totalData={total as number}
          tableData={get(students, "data.data", [])}
          defaultLimit={50}
        />
      </TablePaginationWrapper>
    </>
  );
};
